import { PlusIcon } from "lucide-react";
import { Suspense, useEffect } from "react";

import {
  ConnectionsWithUsageSummaryDocument,
  ConnectionsWithUsageSummaryQuery,
  useGetConnectionBridgesQuery,
} from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import { EmptyState } from "@/components/elements/EmptyState";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { WeldTab } from "@/components/elements/tabs";
import { useDisclosure } from "@/hooks/useDisclosure";
import { AuthorizationLinksTab } from "@/pages/Connections/AuthorizationLinksTab";
import { AuthorizeMethod } from "@/pages/Connections/ConnectionForm/ConnectionForm";
import { useToast } from "@/providers/ToastProvider";
import { useNavigateWithSlug } from "@/router";
import { QueryRef, useBackgroundQuery, useReadQuery } from "@apollo/client";
import { Tab } from "@headlessui/react";
import { useLocation, useSearch } from "@tanstack/react-location";

import { IfRole } from "@/features/roleAccess";

import { SettingsPageLayout } from "../Settings/PageLayout";
import ConnectionsTable from "./ConnectionsTable";
import { NewConnectionSlideOver } from "./NewConnectionSlideOver";

export const ConnectionsPage = () => {
  const { error } = useSearch<{ Search: { error: string } }>();

  const toast = useToast();
  useEffect(() => {
    if (error) {
      toast("Something went wrong", error, "error");
    }
  }, [error, toast]);

  const addConnectionSlideOverDisclosure = useDisclosure<{
    defaultAuthorizeMethod: AuthorizeMethod;
  }>();

  const [connectionsQueryRef, { refetch: refetchConnections }] =
    useBackgroundQuery<ConnectionsWithUsageSummaryQuery>(
      ConnectionsWithUsageSummaryDocument,
    );

  const {
    data: connectionBridges,
    refetch: refetchConnectionBridges,
    error: connectionBridgesError,
    loading: connectionBridgesLoading,
  } = useGetConnectionBridgesQuery({ fetchPolicy: "cache-and-network" });

  return (
    <SettingsPageLayout
      title="Connectors"
      action={
        <IfRole action="createConnection">
          <Button
            onClick={() => addConnectionSlideOverDisclosure.onOpen()}
            tabIndex={-1}
            icon={<PlusIcon className="w-4" />}
            variant="solid"
            colorScheme="primary"
          >
            New Connector
          </Button>
        </IfRole>
      }
    >
      <Tab.Group as="div" className="flex h-full flex-col">
        <Tab.List className="mb-6 flex gap-8">
          <WeldTab groupId="connections-page">Connections</WeldTab>
          <WeldTab groupId="connections-page">Authorization Links</WeldTab>
        </Tab.List>
        <Tab.Panels className="grow">
          <Tab.Panel className="h-full">
            <Suspense fallback={<LoadingSpinner />}>
              <ConnectionsContent
                queryRef={connectionsQueryRef}
                onCreateConnection={() =>
                  addConnectionSlideOverDisclosure.onOpen({
                    defaultAuthorizeMethod: AuthorizeMethod.Self,
                  })
                }
              />
            </Suspense>
          </Tab.Panel>
          <Tab.Panel className="h-full">
            <AuthorizationLinksTab
              connectionBridges={connectionBridges?.connectionBridges ?? []}
              error={connectionBridgesError}
              loading={connectionBridgesLoading}
              onCreateAuthorizationLink={() =>
                addConnectionSlideOverDisclosure.onOpen({
                  defaultAuthorizeMethod: AuthorizeMethod.External,
                })
              }
              onDeleted={refetchConnectionBridges}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      <NewConnectionSlideOver
        size="lg"
        show={addConnectionSlideOverDisclosure.isOpen}
        onClose={() => {
          addConnectionSlideOverDisclosure.onClose();
        }}
        onConnectionAdded={() => refetchConnections()}
        onExternalAuthLinkCreated={() => refetchConnectionBridges()}
        resetOnClose
        enableSelectExistingConnection={false}
        defaultAuthorizeMethod={
          addConnectionSlideOverDisclosure.data?.defaultAuthorizeMethod
        }
      />
    </SettingsPageLayout>
  );
};

function ConnectionsContent(props: {
  queryRef: QueryRef<ConnectionsWithUsageSummaryQuery | undefined>;
  onCreateConnection: () => void;
}) {
  const navigate = useNavigateWithSlug();
  const location = useLocation();

  const { data } = useReadQuery(props.queryRef);
  if (!data?.connectors.length) {
    return (
      <EmptyState
        header="No Connections Found"
        description="A Connector enables you to connect to a data source or destination and start syncing data to and from Weld."
        action={
          <IfRole action="createConnection">
            <Button
              onClick={() => props.onCreateConnection()}
              icon={<PlusIcon />}
            >
              New Connector
            </Button>
          </IfRole>
        }
      />
    );
  }

  const onRowClick = (
    row: ConnectionsWithUsageSummaryQuery["connectors"][0],
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => {
    const to = `/settings/connections/${row.id}`;
    const slug = location.current.pathname.slice(1).split("/")[0];
    if (e.metaKey || e.ctrlKey) {
      window.open(`/${slug}${to}`);
      return;
    }
    navigate({ to: to });
  };

  return <ConnectionsTable data={data.connectors} onRowClick={onRowClick} />;
}
